import type { BreadcrumbItem } from '@/components/layout/Breadcrumbs';
import type { ErpModule } from '@/api/platform.api';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { getModuleBreadcrumbLabel, getModuleEntryByErpModule } from '@/config/moduleEntries';
import { moduleNavigationKey } from '@/i18n/navLabel';
import { resolveRoutePageTitle } from '@/config/routePageTitles';
import { buildSettingsBreadcrumbs, isSettingsPath } from '@/utils/settingsBreadcrumbs';
import { i18n } from '@/i18n';

function normalizePath(pathname: string): string {
  const base = pathname.split('?')[0].replace(/\/+$/, '');
  return base || '/';
}

/** Short module label for breadcrumbs (e.g. HR, Finance, CRM). */
export function resolveModuleBreadcrumbLabel(pathname: string): string | undefined {
  const module = resolveModuleFromPath(pathname);
  if (!module) return undefined;
  const compact =
    getModuleBreadcrumbLabel(module) ?? getModuleEntryByErpModule(module)?.label ?? module;
  const key = `breadcrumbModules.${moduleNavigationKey(module)}`;
  if (i18n.isInitialized) {
    return i18n.t(key, { ns: 'navigation', defaultValue: compact });
  }
  return compact;
}

/**
 * Build module-scoped breadcrumbs from the current route.
 * Pattern: Module home → module name only; other pages → Module > Page (nav label).
 */
export function buildBreadcrumbsFromPath(
  pathname: string,
  tail?: BreadcrumbItem[],
  options?: { activeModule?: ErpModule | null },
): BreadcrumbItem[] {
  const path = normalizePath(pathname);
  if (isSettingsPath(path)) {
    const base = buildSettingsBreadcrumbs(path, { activeModule: options?.activeModule });
    return tail?.length ? [...base, ...tail] : base;
  }
  const module = resolveModuleFromPath(path);
  if (!module) {
    const pageTitle = resolveRoutePageTitle(path);
    const base = pageTitle ? [{ label: pageTitle }] : [];
    return tail?.length ? [...base, ...tail] : base;
  }

  const moduleEntry = getModuleEntryByErpModule(module);
  const moduleHome = moduleEntry?.homePath;
  const moduleLabel = resolveModuleBreadcrumbLabel(path) ?? moduleEntry?.label ?? module;

  const pageTitle = resolveRoutePageTitle(path);
  const isModuleHome = moduleHome != null && path === normalizePath(moduleHome);

  let crumbs: BreadcrumbItem[];
  if (isModuleHome) {
    crumbs = [{ label: moduleLabel, to: moduleHome }];
  } else {
    crumbs = [];
    if (moduleHome) {
      crumbs.push({ label: moduleLabel, to: moduleHome });
    } else {
      crumbs.push({ label: moduleLabel });
    }
    if (pageTitle && pageTitle !== moduleLabel) {
      crumbs.push({ label: pageTitle });
    }
  }

  if (tail?.length) {
    return [...crumbs, ...tail];
  }
  return crumbs;
}

/**
 * Prefer nav/settings-aligned auto crumbs. Hardcoded Module › Page trails are ignored.
 * Entity trails (3+ items, last crumb with no link) append only the last label.
 */
export function mergeBreadcrumbOverride(
  auto: BreadcrumbItem[],
  override?: BreadcrumbItem[],
): BreadcrumbItem[] {
  if (!override?.length) return auto;

  const last = override[override.length - 1];
  if (last?.to) {
    return auto;
  }

  const lastLabel = last?.label?.trim();
  if (!lastLabel) return auto;

  const autoLast = auto[auto.length - 1]?.label?.trim().toLowerCase();
  if (autoLast && lastLabel.toLowerCase() === autoLast) {
    return auto;
  }

  // Entity / dynamic detail: Module › List › Entity
  if (override.length >= 3) {
    return [...auto, { label: lastLabel }];
  }

  return auto;
}

export type ResolvePageChromeBreadcrumbsOptions = {
  activeModule?: ErpModule | null;
  hash?: string;
  /** Legacy full trails from pages; merged carefully against auto crumbs. */
  override?: BreadcrumbItem[];
  /** Explicit suffix after auto Module › Page (preferred for entity pages). */
  tail?: BreadcrumbItem[];
};

/** Single entry used by PageHeader for workspace chrome breadcrumbs. */
export function resolvePageChromeBreadcrumbs(
  pathname: string,
  options: ResolvePageChromeBreadcrumbsOptions = {},
): BreadcrumbItem[] {
  const path = normalizePath(pathname);
  if (isSettingsPath(path)) {
    const base = buildSettingsBreadcrumbs(path, {
      activeModule: options.activeModule,
      hash: options.hash,
    });
    return options.tail?.length ? [...base, ...options.tail] : base;
  }

  const auto = buildBreadcrumbsFromPath(path, options.tail, {
    activeModule: options.activeModule,
  });
  return mergeBreadcrumbOverride(auto, options.override);
}
